import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Mail, User, Phone, Clock, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import CalendarView from './CalendarView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  
  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      loadData();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      
      if (!response.ok) {
        toast.error('Credenziali non valide');
        return;
      }
      
      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('admin_token', data.access_token);
      setIsLoggedIn(true);
      toast.success('Login effettuato!');
    } catch (error) {
      toast.error('Errore durante il login');
    }
  };

  const loadData = async () => {
    try {
      // Load bookings
      const bookingsRes = await fetch(`${API_URL}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const bookingsData = await bookingsRes.json();
      setBookings(bookingsData);

      // Load contacts
      const contactsRes = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const contactsData = await contactsRes.json();
      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsLoggedIn(false);
    localStorage.removeItem('admin_token');
    toast.success('Logout effettuato');
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Panel</h1>
              <p className="text-gray-600">4 KIDS Asilo Nido</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <Input
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="admin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="••••••••"
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-[#E91E8C] hover:bg-[#d01a7a] text-white py-6"
              >
                Accedi
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Username: admin | Password: 4kids2024
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel - 4 KIDS</h1>
              <p className="text-sm text-gray-600">Gestione prenotazioni e contatti</p>
            </div>
            <div className="flex gap-4">
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="border-gray-300"
              >
                Torna al Sito
              </Button>
              <Button 
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setActiveTab('bookings')}
            className={activeTab === 'bookings' ? 'bg-[#E91E8C]' : 'bg-gray-300'}
          >
            📅 Prenotazioni Visite ({bookings.length})
          </Button>
          <Button
            onClick={() => setActiveTab('calendar')}
            className={activeTab === 'calendar' ? 'bg-[#E91E8C]' : 'bg-gray-300'}
          >
            📆 Calendario
          </Button>
          <Button
            onClick={() => setActiveTab('contacts')}
            className={activeTab === 'contacts' ? 'bg-[#E91E8C]' : 'bg-gray-300'}
          >
            📧 Richieste Contatto ({contacts.length})
          </Button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  Nessuna prenotazione ancora
                </CardContent>
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking._id} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <User className="w-5 h-5 text-pink-600" />
                          <h3 className="text-lg font-bold text-gray-900">{booking.parent_name}</h3>
                          <Badge className="bg-green-100 text-green-700">
                            {booking.status}
                          </Badge>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">{booking.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">{booking.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 font-semibold">
                              {formatDate(booking.preferred_date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 font-semibold">
                              {booking.preferred_time}
                            </span>
                          </div>
                        </div>
                        
                        {booking.child_age && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Età bambino:</strong> {booking.child_age}
                          </p>
                        )}
                        
                        {booking.message && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Messaggio:</strong> {booking.message}
                          </p>
                        )}
                        
                        <p className="mt-3 text-xs text-gray-400">
                          Ricevuta il: {new Date(booking.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <CalendarView bookings={bookings} />
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  Nessuna richiesta di contatto ancora
                </CardContent>
              </Card>
            ) : (
              contacts.map((contact) => (
                <Card key={contact._id} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <User className="w-5 h-5 text-pink-600" />
                          <h3 className="text-lg font-bold text-gray-900">{contact.parent_name}</h3>
                          <Badge className="bg-blue-100 text-blue-700">
                            {contact.status}
                          </Badge>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">{contact.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">{contact.phone}</span>
                          </div>
                        </div>
                        
                        {contact.child_age && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Età bambino:</strong> {contact.child_age}
                          </p>
                        )}
                        
                        {contact.start_period && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Inizio frequenza:</strong> {contact.start_period}
                          </p>
                        )}
                        
                        {contact.message && (
                          <p className="mt-2 text-sm text-gray-600">
                            <strong>Messaggio:</strong> {contact.message}
                          </p>
                        )}
                        
                        <p className="mt-3 text-xs text-gray-400">
                          Ricevuta il: {new Date(contact.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
